import {html} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {createRef, ref} from "lit/directives/ref.js";
import {when} from "lit/directives/when.js";
import {AbstractLitInputComponent} from "../../abstract-lit-input.component";

/**
 * Single-file picker.
 *
 * This is the ONLY place a native `<input type="file">` is allowed (ADR-012):
 * every consumer binds `.value` and listens for the house `value-changed`
 * CustomEvent, whose `detail.value` carries the selected `File` — or `null`
 * when the selection was cleared.
 */
@customElement(`ha-input-file`)
export class HaInputFileComponent extends AbstractLitInputComponent<File> {

    /** Comma-separated MIME whitelist handed to the native picker. */
    @property({type: String})
    public accept: string = `image/png,image/jpeg,image/webp,image/gif`;

    private _inputRef = createRef<HTMLInputElement>();

    public focus(options?: FocusOptions): void {
        if (this._inputRef.value instanceof HTMLInputElement) {
            this._inputRef.value.focus(options);
        }
    }

    /**
     * Translate the native `change` event into the house `value-changed` event.
     * `files` may be absent or empty (selection cleared) — both yield `null`.
     */
    protected _onChange(evt: Event): void {
        if (this.readonly) {
            return;
        }

        const files = (evt.target as HTMLInputElement).files;
        this.value = files && files.length > 0 ? files[0] : null;
        this.fire(this.value);
    }

    protected render(): unknown {
        return html`
            ${when(!!this.label, () => html`
                <label>${this.label}</label>
            `)}
            <div class="wrapper">
                <input type="file"
                       accept=${this.accept}
                       @change=${this._onChange}
                       ?disabled=${this.readonly}
                       ${ref(this._inputRef)}
                >
            </div>
            ${this._renderHelperText()}
        `;
    }
}
